import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Output,
  PLATFORM_ID,
} from '@angular/core';
import {
  authActions,
  chatActions,
  ChatModalComponent,
  CoreAppState,
  selectAuth,
  selectChat,
  toggleSidenavIsOpen,
} from '@aa/angular/core';
import {
  BehaviorSubject,
  combineLatestWith,
  firstValueFrom,
  map,
  Observable,
  switchMap,
} from 'rxjs';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { formatDateTime, isInStandaloneMode } from '@aa/ts/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { ToastrService } from 'ngx-toastr';
import {
  MessageResourceTypeMappings,
  ThreadResourceTypeMappings,
} from '@aa/nest/resource';
import { getDateTimeFromOther } from 'src/libs/ts/common/src/lib/date-time/get-date-time-from-other';
import { DateTime } from 'luxon';
import { MatDialog } from '@angular/material/dialog';

export const APP_VERSION = 'V2.1.0';

const firebaseConfig = {
  apiKey: 'AIzaSyCSdJ2AKJcRl-uYULIKuFQnRcA-QJTEmm8',
  authDomain: 'all-angles-428811.firebaseapp.com',
  projectId: 'all-angles-428811',
  storageBucket: 'all-angles-428811.firebasestorage.app',
  messagingSenderId: '834934925350',
  appId: '1:834934925350:web:00e94652ba37abec399ee7',
  measurementId: 'G-PYFCF3T3BS',
};

@Component({
  selector: 'aa-app-bar',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
  ],
  templateUrl: './app-bar.component.html',
  styleUrl: './app-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppBarComponent implements AfterViewChecked {
  @Output() showInstallPrompt = new EventEmitter();

  username$: Observable<string>;
  isStaffApp$: Observable<boolean>;

  latestMessages$: Observable<
    {
      thread: ThreadResourceTypeMappings['resourceWithRelationsT'];
      message: MessageResourceTypeMappings['resourceWithRelationsT'];
    }[]
  >;
  unreadMessagesCount$: Observable<number>;

  appVersion = APP_VERSION;

  showInstallAppButton$ = new BehaviorSubject(true);

  constructor(
    private readonly store: Store<CoreAppState>,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly toastr: ToastrService,
    private readonly dialog: MatDialog,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.setupPushNotifications();
    }

    this.username$ = this.store
      .select((s) => s.isStaffApp)
      .pipe(
        switchMap((isStaffApp) =>
          this.store
            .select((s) => selectAuth(s).user)
            .pipe(
              map((user) =>
                isStaffApp
                  ? `${user?.staffProfile?.firstName ?? ''} ${
                      user?.staffProfile?.lastName ?? ''
                    }`
                  : `${user?.customerProfile?.username ?? ''}`,
              ),
            ),
        ),
        takeUntilDestroyed(),
      );

    this.isStaffApp$ = this.store
      .select((s) => s.isStaffApp)
      .pipe(takeUntilDestroyed());

    this.latestMessages$ = this.store
      .select((s) => selectChat(s).activeThreads)
      .pipe(
        map((threads) =>
          threads
            .map((thread) => {
              // const messages = thread.messages.filter((m) =>
              //   // m.readAt == null &&
              //   isStaffApp
              //     ? m.customerProfileId !== null
              //     : m.customerProfileId == null,
              // );
              const messages = structuredClone(thread.messages);
              messages.sort((a, b) =>
                getDateTimeFromOther(a.sentAt)!
                  .diff(getDateTimeFromOther(b.sentAt)!)
                  .as('seconds'),
              );

              return {
                thread: thread.thread,
                message: messages.at(
                  -1,
                ) as MessageResourceTypeMappings['resourceWithRelationsT'],
              };
            })
            .sort((a, b) => {
              if (!a.message) {
                return -1;
              }
              if (!b.message) {
                return 1;
              }

              return getDateTimeFromOther(b.message.sentAt)!
                .diff(getDateTimeFromOther(a.message.sentAt)!)
                .as('seconds');
            })
            .filter((m) => !!m.message),
        ),
      );
    this.unreadMessagesCount$ = this.latestMessages$.pipe(
      combineLatestWith(this.isStaffApp$),
      map(
        ([latestMessages, isStaffApp]) =>
          latestMessages.filter(
            (m) =>
              (isStaffApp
                ? !!m.message.customerProfileId
                : !m.message.customerProfileId) && !m.message.readAt,
          ).length ?? 0,
      ),
    );

    this.isStaffApp$.subscribe((isStaffApp) => {
      if (isStaffApp) {
        this.store.dispatch(chatActions.connectToStaffThreads());
      } else {
        this.store.dispatch(chatActions.connectToCustomerThreads());
      }
    });

    this.route.queryParamMap
      .pipe(takeUntilDestroyed())
      .subscribe((queryParamMap) => {
        console.log('TEST!!!');
        const threadCustomerProfileId = queryParamMap.get(
          'threadCustomerProfileId',
        );
        if (threadCustomerProfileId) {
          this.dialog.open(ChatModalComponent, {
            data: {
              customerProfileId: +threadCustomerProfileId,
            },
            height: 'fit-content',
            maxHeight: 'fit-content',
            minWidth: '50vw',
            maxWidth: 'calc(100dvw - 5rem)',
            width: '50rem',
          });
        }
      });
  }

  ngAfterViewChecked() {
    if (isInStandaloneMode()) {
      this.showInstallAppButton$.next(false);
    }
  }

  toggleSidenav() {
    this.store.dispatch(toggleSidenavIsOpen());
  }

  installApp() {
    this.showInstallPrompt.emit();
  }

  logout() {
    this.store.dispatch(authActions.logout());
  }

  async goToThreadForCustomer(customerProfileId: number) {
    this.router.navigate([], {
      queryParams: {
        threadCustomerProfileId: customerProfileId,
      },
      onSameUrlNavigation: 'reload',
      relativeTo: this.route,
    });
  }

  async goToThreadForOrderCard(orderId: number, orderCardId: number) {
    const isStaffApp = await firstValueFrom(this.isStaffApp$);

    this.router.navigate([isStaffApp ? 'orders' : 'order-review', orderId], {
      queryParams: {
        threadOrderCardId: orderCardId,
      },
      onSameUrlNavigation: 'reload',
    });
  }

  async setupPushNotifications() {
    const app = initializeApp(firebaseConfig);
    const messaging = getMessaging(app);

    const permission = await Notification.requestPermission();

    if (permission == 'granted') {
      const token = await getToken(messaging, {
        vapidKey:
          'BPRWyzFmMOrz-qkTauBEJmtz8l3WprcBSlltnKZLcLAFwkq5KoUZe4aSxjsJwQegxCCcGZavFl5aiDXFB4zzwkE',
      });
      if (token) {
        this.store.dispatch(authActions.setNotificationToken({ token }));

        onMessage(messaging, (payload) => {
          this.toastr
            .info(payload.notification?.body, payload.notification?.title, {
              closeButton: true,
              disableTimeOut: true,
            })
            .onTap.subscribe(() => {
              if (
                payload.fcmOptions?.link &&
                window.location.href != payload.fcmOptions?.link
              )
                window.location.href = payload.fcmOptions?.link;
            });
        });
      }
    }
  }

  formatSentAt(timestamp: string | Date | DateTime) {
    return formatDateTime(timestamp, true);
  }
}
